// Chemin: C:\gestion-copro\src\services\pdf.service.ts
// Service de génération PDF pour ORION PILOT

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Signalement } from '../types/signalement.types';

// ============================================
// TYPES
// ============================================

interface PDFExportOptions {
  titre?: string;
  sousTitre?: string;
  includePhotos?: boolean;
}

// ============================================
// CONSTANTES
// ============================================

const COLORS = {
  primary: [30, 58, 95] as [number, number, number],      // Bleu foncé #1E3A5F
  secondary: [74, 144, 217] as [number, number, number],  // Bleu clair #4A90D9
  text: [44, 62, 80] as [number, number, number],         // Gris foncé
  lightGray: [245, 246, 250] as [number, number, number], // Fond gris
  white: [255, 255, 255] as [number, number, number],
};

const URGENCE_LABELS: Record<string, string> = {
  basse: 'Basse',
  moyenne: 'Moyenne',
  haute: 'Haute',
  critique: 'Critique'
};

const STATUT_LABELS: Record<string, string> = {
  nouveau: 'Nouveau',
  en_cours: 'En cours',
  transmis_syndic: 'Transmis syndic',
  transmis_promoteur: 'Transmis promoteur',
  resolu: 'Résolu',
  refuse: 'Refusé'
};

const LOCALISATION_LABELS: Record<string, string> = {
  parties_communes: 'Parties communes',
  parties_privatives: 'Parties privatives',
  equipements: 'Équipements'
};

// ============================================
// FONCTIONS UTILITAIRES
// ============================================

/**
 * Formater une date Firestore en string lisible
 */
const formatDate = (timestamp: any): string => {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/**
 * Formater une date longue
 */
const formatDateLong = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ============================================
// GÉNÉRATION PDF
// ============================================

/**
 * Générer un PDF de synthèse des signalements
 */
export const generateSignalementsPDF = async (
  signalements: Signalement[],
  options: PDFExportOptions = {}
): Promise<void> => {
  const {
    titre = 'Synthèse des Signalements',
    sousTitre = 'Copropriété',
  } = options;

  // Créer le document PDF (A4)
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let currentY = margin;

  // ============================================
  // EN-TÊTE
  // ============================================

  // Fond bleu en-tête
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 45, 'F');

  // Titre principal
  doc.setTextColor(...COLORS.white);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Copro Protect', margin, 18);

  // Sous-titre
  doc.setFontSize(14);
  doc.setFont('helvetica', 'normal');
  doc.text(titre, margin, 28);

  // Nom copropriété
  doc.setFontSize(11);
  doc.text(sousTitre, margin, 36);

  // Date de génération (à droite)
  doc.setFontSize(9);
  const dateGeneration = `Généré le ${formatDateLong(new Date())}`;
  const dateWidth = doc.getTextWidth(dateGeneration);
  doc.text(dateGeneration, pageWidth - margin - dateWidth, 36);

  currentY = 55;

  // ============================================
  // STATISTIQUES
  // ============================================

  // Calculer les stats
  const stats = {
    total: signalements.length,
    nouveaux: signalements.filter(s => s.statut === 'nouveau').length,
    enCours: signalements.filter(s => ['en_cours', 'transmis_syndic', 'transmis_promoteur'].includes(s.statut)).length,
    resolus: signalements.filter(s => s.statut === 'resolu').length,
    refuses: signalements.filter(s => s.statut === 'refuse').length,
  };

  // Titre section
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Statistiques', margin, currentY);
  currentY += 8;

  // Boîtes de stats
  const boxWidth = (pageWidth - 2 * margin - 15) / 4;
  const boxHeight = 20;
  const statsData = [
    { label: 'Total', value: stats.total, color: COLORS.secondary },
    { label: 'Nouveaux', value: stats.nouveaux, color: [231, 76, 60] as [number, number, number] },
    { label: 'En cours', value: stats.enCours, color: [241, 196, 15] as [number, number, number] },
    { label: 'Résolus', value: stats.resolus, color: [39, 174, 96] as [number, number, number] },
  ];

  statsData.forEach((stat, index) => {
    const x = margin + index * (boxWidth + 5);
    
    // Fond de la boîte
    doc.setFillColor(...COLORS.lightGray);
    doc.roundedRect(x, currentY, boxWidth, boxHeight, 3, 3, 'F');
    
    // Indicateur de couleur
    doc.setFillColor(...stat.color);
    doc.rect(x, currentY, 3, boxHeight, 'F');
    
    // Valeur
    doc.setTextColor(...COLORS.text);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(String(stat.value), x + 10, currentY + 9);
    
    // Label
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(stat.label, x + 10, currentY + 15);
  });

  currentY += boxHeight + 15;

  // ============================================
  // ALERTE GPA
  // ============================================

  const dateExpirationGPA = new Date('2026-09-18');
  const joursRestants = Math.ceil((dateExpirationGPA.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

  doc.setFillColor(255, 243, 224); // Orange clair
  doc.roundedRect(margin, currentY, pageWidth - 2 * margin, 15, 2, 2, 'F');
  
  doc.setTextColor(230, 126, 34); // Orange
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`/!\\ Garantie de Parfait Achevement : expiration dans ${joursRestants} jours (${formatDate(dateExpirationGPA)})`, margin + 5, currentY + 9);

  currentY += 22;

  // ============================================
  // TABLEAU DES SIGNALEMENTS
  // ============================================

  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Liste des Signalements', margin, currentY);
  currentY += 5;

  // Préparer les données du tableau
  const tableData = signalements.map((sig, index) => [
    String(index + 1),
    formatDate(sig.dateSignalement),
    sig.titre.length > 35 ? sig.titre.substring(0, 35) + '...' : sig.titre,
    LOCALISATION_LABELS[sig.localisation] || sig.localisation,
    sig.localisationDetail.length > 20 ? sig.localisationDetail.substring(0, 20) + '...' : sig.localisationDetail,
    URGENCE_LABELS[sig.urgence] || sig.urgence,
    STATUT_LABELS[sig.statut] || sig.statut,
  ]);

  // Générer le tableau
  autoTable(doc, {
    startY: currentY,
    head: [['#', 'Date', 'Titre', 'Type', 'Localisation', 'Urgence', 'Statut']],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: COLORS.primary,
      textColor: COLORS.white,
      fontSize: 9,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 8,
      textColor: COLORS.text,
    },
    alternateRowStyles: {
      fillColor: COLORS.lightGray,
    },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 22 },
      2: { cellWidth: 45 },
      3: { cellWidth: 28 },
      4: { cellWidth: 30 },
      5: { cellWidth: 18, halign: 'center' },
      6: { cellWidth: 25, halign: 'center' },
    },
    margin: { left: margin, right: margin },
    didParseCell: (data) => {
      // Colorer les urgences
      if (data.column.index === 5 && data.section === 'body') {
        const urgence = signalements[data.row.index]?.urgence;
        if (urgence === 'critique') {
          data.cell.styles.textColor = [192, 57, 43];
          data.cell.styles.fontStyle = 'bold';
        } else if (urgence === 'haute') {
          data.cell.styles.textColor = [211, 84, 0];
        }
      }
      // Colorer les statuts
      if (data.column.index === 6 && data.section === 'body') {
        const statut = signalements[data.row.index]?.statut;
        if (statut === 'nouveau') {
          data.cell.styles.textColor = [192, 57, 43];
        } else if (statut === 'resolu') {
          data.cell.styles.textColor = [39, 174, 96];
        }
      }
    },
  });

  // Récupérer la position Y après le tableau
  const finalY = (doc as any).lastAutoTable.finalY || currentY + 50;

  // ============================================
  // DÉTAILS DES SIGNALEMENTS (si peu nombreux)
  // ============================================

  if (signalements.length <= 10) {
    let detailY = finalY + 15;

    // Vérifier s'il faut une nouvelle page
    if (detailY > pageHeight - 60) {
      doc.addPage();
      detailY = margin;
    }

    doc.setTextColor(...COLORS.primary);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Détail des Signalements', margin, detailY);
    detailY += 8;

    for (const sig of signalements) {
      // Vérifier s'il faut une nouvelle page
      if (detailY > pageHeight - 50) {
        doc.addPage();
        detailY = margin;
      }

      // Fond de la fiche
      doc.setFillColor(...COLORS.lightGray);
      doc.roundedRect(margin, detailY, pageWidth - 2 * margin, 35, 2, 2, 'F');

      // Titre du signalement
      doc.setTextColor(...COLORS.primary);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(sig.titre, margin + 5, detailY + 7);

      // Infos ligne 1
      doc.setTextColor(...COLORS.text);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const ligne1 = `Date: ${formatDate(sig.dateSignalement)} | Localisation: ${sig.localisationDetail} | Catégorie: ${sig.categorie}`;
      doc.text(ligne1, margin + 5, detailY + 14);

      // Infos ligne 2
      const ligne2 = `Urgence: ${URGENCE_LABELS[sig.urgence]} | Statut: ${STATUT_LABELS[sig.statut]} | Déclarant: ${sig.declarant.nom}`;
      doc.text(ligne2, margin + 5, detailY + 21);

      // Description (tronquée)
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      const descTronquee = sig.description.length > 120 ? sig.description.substring(0, 120) + '...' : sig.description;
      doc.text(descTronquee, margin + 5, detailY + 28, { maxWidth: pageWidth - 2 * margin - 10 });

      // Photos indicateur
      if (sig.photos && sig.photos.length > 0) {
        doc.setTextColor(...COLORS.secondary);
        doc.text(`${sig.photos.length} photo(s)`, pageWidth - margin - 25, detailY + 7);
      }

      detailY += 40;
    }
  }

  // ============================================
  // PIED DE PAGE
  // ============================================

  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Ligne de séparation
    doc.setDrawColor(...COLORS.lightGray);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);
    
    // Texte pied de page
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(8);
    doc.text('Copro Protect - Gestion de Copropriété', margin, pageHeight - 8);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin - 20, pageHeight - 8);
  }

  // ============================================
  // SAUVEGARDER
  // ============================================

  const dateStr = new Date().toISOString().split('T')[0];
  doc.save(`CoproProtect_Signalements_${dateStr}.pdf`);
};

/**
 * Générer un PDF pour un signalement unique
 */
export const generateSignalementDetailPDF = async (
  signalement: Signalement,
  nomCopropriete?: string
): Promise<void> => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let currentY = margin;

  const nomRes = nomCopropriete || 'Copropriété';

  // En-tête
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, pageWidth, 35, 'F');

  doc.setTextColor(...COLORS.white);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text('Copro Protect - Fiche Signalement', margin, 15);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(`${nomRes} - ${signalement.numeroSignalement || signalement.id}`, margin, 25);

  currentY = 45;

  // Titre du signalement
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(signalement.titre, margin, currentY);
  currentY += 12;

  // Informations générales
  const infos = [
    ['Date de signalement', formatDate(signalement.dateSignalement)],
    ['Localisation', `${LOCALISATION_LABELS[signalement.localisation]} - ${signalement.localisationDetail}`],
    ['Catégorie', signalement.categorie],
    ['Urgence', URGENCE_LABELS[signalement.urgence]],
    ['Statut', STATUT_LABELS[signalement.statut]],
    ['Déclarant', signalement.declarant.nom],
    ['Contact', signalement.declarant.email || signalement.declarant.telephone || 'Non renseigné'],
  ];

  doc.setFontSize(10);
  infos.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.text);
    doc.text(`${label}:`, margin, currentY);
    doc.setFont('helvetica', 'normal');
    doc.text(value, margin + 45, currentY);
    currentY += 7;
  });

  currentY += 5;

  // Description
  doc.setTextColor(...COLORS.primary);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Description', margin, currentY);
  currentY += 7;

  doc.setTextColor(...COLORS.text);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const splitDescription = doc.splitTextToSize(signalement.description, pageWidth - 2 * margin);
  doc.text(splitDescription, margin, currentY);

  // Sauvegarder
  const dateStr = new Date().toISOString().split('T')[0];
  const fileName = signalement.numeroSignalement || signalement.id;
  doc.save(`CoproProtect_${fileName}_${dateStr}.pdf`);
};